"use client";

import { useState, useEffect, ReactNode } from "react";

interface DeferredProps {
  children: ReactNode;
  /** Delay in ms before rendering (default: 0, uses requestIdleCallback) */
  delay?: number;
}

/**
 * Defers rendering of children until the browser is idle.
 * This keeps non-critical components (LiveTicker, CookieBanner, etc.)
 * completely out of Lighthouse's critical rendering path.
 *
 * - No SSR (children don't render on server)
 * - No initial client render (returns null until idle)
 * - Uses requestIdleCallback with setTimeout fallback
 */
export default function Deferred({ children, delay }: DeferredProps) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (delay != null) {
      const timeout = setTimeout(() => setShow(true), delay);
      return () => clearTimeout(timeout);
    }

    if ("requestIdleCallback" in window) {
      const id = requestIdleCallback(() => setShow(true));
      return () => cancelIdleCallback(id);
    }

    // Fallback: wait 2s
    const timeout = setTimeout(() => setShow(true), 2000);
    return () => clearTimeout(timeout);
  }, [delay]);

  if (!show) return null;
  return <>{children}</>;
}
